import React, { useState, useEffect } from 'react';
import ReportPage from '../ReportChart/ReportPage';
import { useChartColors } from '../ReportChart/hooks';
import { getAPIResponse } from 'helpers/getApiResponse';
import { Apis } from 'services/api';
import API from "helpers/api";
import { getDefectStatuses } from "services/clients/sites/siteApplications/defectStatuses";
import { getSiteApplicationDetail } from 'services/clients/sites/siteApplications/siteApplicationDetails';

// Helper function to build department IDs query string
const buildDepartmentQuery = (siteDepartmentId = "", siteDepartmentIds = []) => {
	if (siteDepartmentIds && siteDepartmentIds.length > 0) {
		const query = siteDepartmentIds.map(id => `siteDepartmentIds=${id}`).join('&');
		return query ? `&${query}` : "";
	} else if (siteDepartmentId) {
		return `&siteDepartmentId=${siteDepartmentId ?? ""}`;
	}
	return "";
};

const getCompletedVsOutstanding = async ({
	defectTypeId = "",
	startDate = "",
	endDate = "",
	defectRiskRatingId = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
	defectStatusType = "",
	defectStatusID = "",
}) => {
  try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
    let response = await API.get(
			`${Apis.Analytics}/completedvsoutstandingmodified?defectTypeId=${defectTypeId}&startDate=${startDate}&endDate=${endDate}&defectRiskRatingId=${defectRiskRatingId}${deptQuery}&modelId=${modelId}&defectStatusType=${defectStatusType}&defectStatusID=${defectStatusID}`
		);
    return getAPIResponse(response);
  } catch (err) {
    return getAPIResponse(err?.response);
  }
};

const CompletedVsOutstandingReport = () => {
  const { customCaptions, siteAppID } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const chartColors = useChartColors();
  const [defectStatuses, setDefectStatuses] = useState([]);
  const [siteAppDetail, setSiteAppDetail] = useState(null);

  useEffect(() => {
    const fetchSiteApplicationDetails = async () => {
      try {
        const result = await getSiteApplicationDetail(siteAppID);
        setSiteAppDetail(result.data);
      } catch (error) {
        console.error("Error fetching site application details:", error);
      }
    };
    fetchSiteApplicationDetails();
  }, [siteAppID]);

  useEffect(() => {
    const fetchDefectStatuses = async () => {
      try {
        const statuses = await getDefectStatuses();
        if (statuses?.data) {
          setDefectStatuses(statuses.data);
        }
      } catch (error) {
        console.error("Error fetching defect statuses:", error);
      }
    };

    fetchDefectStatuses();
  }, []);

  // Helper function to detect if data contains daily information
  const detectDataFormat = (responseData) => {
    if (!responseData || !Array.isArray(responseData)) return 'monthly';
    
    // Check the first status item's data
    const firstStatus = responseData.find(item => item.data && Array.isArray(item.data) && item.data.length > 0);
    if (!firstStatus) return 'monthly';
    
    // Check if any data point has a 'day' property
    const hasDay = firstStatus.data.some(point => point.day !== undefined && point.day !== null);
    return hasDay ? 'daily' : 'monthly';
  };

  const chartConfig = {
    id: 'completed-vs-outstanding',
    title: `${customCaptions?.defectPlural} by Status`,
    fetchData: async (params) => {
      return await getCompletedVsOutstanding(params);
    },
    allowedChartTypes: ['line', 'vertical-bar', 'stacked-bar', 'horizontal-stacked-bar'],
    defaultChartType: 'line',
    // Dynamic axis configuration will be set in transformData
    axisConfig: {
      categoryAxis: { 
        dataKey: 'date'
      },
      valueAxes: [{ 
        label: 'Count'
      }]
    },
    series: () => {
      if (!defectStatuses.length) {
        return [];
      }

      // Generate dynamic series based on defect statuses
      return defectStatuses.map((status, index) => {
        // Determine color type based on rules:
        // 1. Series of type 'C' are positive
        // 2. Series where ID matches the defaultDefectStatusId are negative
        // 3. All others are neutral
        let colorType = 'neutral';
        if (status.type === 'C') {
          colorType = 'positive';
        } else if (status.id === siteAppDetail?.defaultDefectStatusID) {
          colorType = 'negative';
        }
        
        // Get color from appropriate palette
        const color = chartColors.getColorByIndex(index % 5, colorType);

        return {
          label: status.name,
          key: status.name,
          showLegend: true,
          colors: [color],
          defaultColor: color,
          getColorByIndex: () => color,
          drilldown: true,
          seriesMetadata: { type: status.type, id: status.id }
        };
      });
    },
    transformData: (response) => {
      if (!response?.data || !Array.isArray(response.data)) {
        return [];
      }

      // Detect data format (daily vs monthly)
      const dataFormat = detectDataFormat(response.data);
      console.log('Detected data format:', dataFormat);

      // Dynamically update axis configuration based on data format
      if (dataFormat === 'daily') {
        chartConfig.axisConfig.categoryAxis.type = 'date';
      } else {
        // For monthly data, use category axis to show month labels properly
        chartConfig.axisConfig.categoryAxis.type = 'category';
      }

      // Create a map to consolidate data by date
      const dateMap = new Map();

      // Process each status and its data
      response.data.forEach(item => {
        if (item.statusName && Array.isArray(item.data)) {
          console.log(`Processing status: ${item.statusName}`, { 
            sampleData: item.data.slice(0, 3),
            format: dataFormat
          });
          
          item.data.forEach(point => {
            const dateKey = point.label;
            let dateObj, displayDate;

            if (dataFormat === 'daily') {
              // For daily data: create date from year/month/day
              dateObj = new Date(point.year, point.month - 1, point.day);
              displayDate = dateObj.toLocaleDateString(navigator.language, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
            } else {
              // For monthly data: create date from year/month (first day of month)
              dateObj = new Date(point.year, point.month - 1, 1);
              displayDate = dateObj.toLocaleDateString(navigator.language, {
                month: 'short',
                year: 'numeric'
              });
            }
            
            const existingPoint = dateMap.get(dateKey) || {
              label: point.label,
              date: dataFormat === 'daily' ? dateObj.getTime() : dateKey, // Use timestamp for daily, label for monthly
              displayDate: displayDate,
              year: point.year,
              month: point.month,
              ...(dataFormat === 'daily' && { day: point.day })
            };
            
            // Store the count under the status name key
            // Ensure the count is a number (0 if undefined/null)
            existingPoint[item.statusName] = point.count || 0;
            
            dateMap.set(dateKey, existingPoint);
          });
        }
      });

      // Check if we have collected any data
      if (dateMap.size === 0) {
        console.warn('No data collected during transformation');
        return [];
      }

      // Convert map to array and sort appropriately
      const result = Array.from(dateMap.values());
      
      if (dataFormat === 'daily') {
        // Sort by date timestamp for daily data
        result.sort((a, b) => a.date - b.date);
      } else {
        // Sort by year/month for monthly data
        result.sort((a, b) => {
          if (a.year !== b.year) {
            return a.year - b.year;
          }
          return a.month - b.month;
        });
      }

      // Log some debug info
      console.log('Transformed data sample:', result.slice(0, 3));
      console.log('Applied axis config:', chartConfig.axisConfig.categoryAxis);
      
      return result;
    },
    filters: {
      timeframe: {
        allowCustomRange: true,
        defaultRange: 'show-all',
      },
      department: { multiSelect: true },
      model: true,
      riskRatings: true
    },
    drilldown: {
      defaultChartType: 'horizontal-bar',
      allowedChartTypes: ['horizontal-bar', 'pie'],
      viewData: null,
      axisConfig: {
        categoryAxis: { 
          dataKey: 'label'
        },
        valueAxes: [{
          label: `${customCaptions?.asset || 'Asset'} Count`
        }]
      },
      series: [
        {
          label: `${customCaptions?.asset || 'Asset'} Count`,
          key: 'count',
          showLegend: false,
          type: 'bar'
        }
      ],
      transformData: (response) => {
        return response?.data;
      },
      currentDrilldownLabel: `${customCaptions?.asset || 'Asset'} Count`,
      getParams: (dataPoint) => {
        return {
          defectStatusID: dataPoint?.seriesMetadata?.id || '',
        };
      }
    },
    viewData: '/app/defects'
  };
  return (
    <ReportPage
      title={`Completed vs Outstanding ${customCaptions?.defectPlural}`}
      chartConfig={chartConfig}
    />
  );
};

export default CompletedVsOutstandingReport; 
